import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import toast from 'react-hot-toast'
import confetti from 'canvas-confetti'
import { supabase } from '../lib/supabase'
import { createUserScopedStorage } from '../lib/userScopedStorage'
import { useAuth } from './AuthContext'
import type { ActivityKind } from '../config/activityTypes'

interface ActiveAnalysis {
  id: string
  kind: ActivityKind
  type: 'flash' | 'approfondi'
  startedAt: number
  status: 'processing' | 'completed' | 'failed'
  scoreGlobal?: number
}

interface ActiveAnalysisContextType {
  analyses: ActiveAnalysis[]
  addAnalysis: (id: string, type: 'flash' | 'approfondi') => void
  addActivity: (id: string, kind: ActivityKind, type?: 'flash' | 'approfondi') => void
  dismissAnalysis: (id: string) => void
  completedAnalysis: ActiveAnalysis | null
  dismissCompleted: () => void
}

const ActiveAnalysisContext = createContext<ActiveAnalysisContextType | null>(null)

/** Logical key under the user-scoped storage prefix. */
const STORAGE_KEY = 'active-analyses'

/** Analyses older than this are considered stale and dropped on load. */
const STALE_AFTER_MS = 10 * 60 * 1000

function filterFreshProcessing(raw: ActiveAnalysis[]): ActiveAnalysis[] {
  const cutoff = Date.now() - STALE_AFTER_MS
  return raw
    .filter(a => a.startedAt > cutoff && a.status === 'processing')
    .map(a => ({ ...a, kind: a.kind ?? ('analysis' as ActivityKind) }))
}

export function ActiveAnalysisProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const userId = user?.id ?? null

  // Scoped storage instance, rebuilt when the signed-in user changes.
  // Kept in a ref so callbacks can stay stable across renders.
  const storageRef = useRef(createUserScopedStorage(userId))
  useEffect(() => {
    storageRef.current = createUserScopedStorage(userId)
  }, [userId])

  const [analyses, setAnalyses] = useState<ActiveAnalysis[]>([])
  const [completedAnalysis, setCompletedAnalysis] = useState<ActiveAnalysis | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Ref to access current analyses inside polling without re-creating the effect
  const analysesRef = useRef(analyses)
  analysesRef.current = analyses

  // Hydrate local state from the (user-scoped) storage whenever the
  // signed-in user changes. On sign-out we reset everything so nothing
  // from the previous user can leak to the next one.
  useEffect(() => {
    if (!userId) {
      setAnalyses([])
      setCompletedAnalysis(null)
      return
    }
    const scoped = createUserScopedStorage(userId)
    const raw = scoped.getJSON<ActiveAnalysis[]>(STORAGE_KEY, [])
    setAnalyses(filterFreshProcessing(raw))
    setCompletedAnalysis(null)
  }, [userId])

  const saveToStorage = useCallback((next: ActiveAnalysis[]) => {
    const processing = next.filter(a => a.status === 'processing')
    if (processing.length === 0) {
      storageRef.current.remove(STORAGE_KEY)
    } else {
      storageRef.current.setJSON(STORAGE_KEY, processing)
    }
  }, [])

  const addAnalysis = useCallback((id: string, type: 'flash' | 'approfondi') => {
    const newAnalysis: ActiveAnalysis = {
      id,
      kind: 'analysis',
      type,
      startedAt: Date.now(),
      status: 'processing',
    }
    setCompletedAnalysis(null)
    setAnalyses(prev => {
      const updated = [...prev.filter(a => a.id !== id && a.status === 'processing'), newAnalysis]
      saveToStorage(updated)
      return updated
    })
  }, [saveToStorage])

  const addActivity = useCallback((id: string, kind: ActivityKind, type: 'flash' | 'approfondi' = 'flash') => {
    const newAnalysis: ActiveAnalysis = {
      id,
      kind,
      type,
      startedAt: Date.now(),
      status: 'processing',
    }
    setCompletedAnalysis(null)
    setAnalyses(prev => {
      const updated = [...prev.filter(a => a.id !== id && a.status === 'processing'), newAnalysis]
      saveToStorage(updated)
      return updated
    })
  }, [saveToStorage])

  const dismissAnalysis = useCallback((id: string) => {
    setAnalyses(prev => {
      const updated = prev.filter(a => a.id !== id)
      saveToStorage(updated)
      return updated
    })
  }, [saveToStorage])

  // Dismissing the completion notification also drops every 'completed'
  // entry from the array so the in-memory state does not grow over the
  // course of a session. The pill already filters on status==='processing'
  // for its main render, but the leftover 'completed' rows were stale
  // state that nothing observed after dismissal — pure dead weight.
  const dismissCompleted = useCallback(() => {
    setCompletedAnalysis(null)
    setAnalyses(prev => {
      const updated = prev.filter(a => a.status !== 'completed')
      saveToStorage(updated)
      return updated
    })
  }, [saveToStorage])

  // Auto-dismiss completion notification after 30s (routes through
  // dismissCompleted so the array cleanup fires on this path too).
  useEffect(() => {
    if (!completedAnalysis) return
    const timer = setTimeout(() => dismissCompleted(), 30_000)
    return () => clearTimeout(timer)
  }, [completedAnalysis, dismissCompleted])

  // Stable dependency: only the set of processing IDs (not the full analyses state)
  // Effect only re-runs when an ID is added or removed from processing
  const processingIds = useMemo(() => {
    return analyses
      .filter(a => a.status === 'processing')
      .map(a => a.id)
      .sort()
      .join(',')
  }, [analyses])

  // Poll for active analyses — batched query, concurrency-safe, abort-safe
  useEffect(() => {
    if (!processingIds) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }

    const abortController = new AbortController()
    let inFlight = false
    let consecutiveErrors = 0

    const pollFn = async () => {
      // Concurrency guard + abort guard
      if (inFlight || abortController.signal.aborted) return
      inFlight = true

      try {
        const ids = processingIds.split(',')

        // Single batched query — abortSignal prevents dangling requests
        // that could hold the Supabase auth lock after cleanup
        const { data, error } = await supabase
          .from('analyses')
          .select('status, analysis_id, score_global')
          .in('analysis_id', ids)
          .abortSignal(abortController.signal)

        if (error || !data || abortController.signal.aborted) return
        consecutiveErrors = 0
        // Successful poll — clear the one-shot reload guard so a future
        // genuine failure can trigger a new recovery reload.
        try {
          sessionStorage.removeItem('__bc_active_analysis_reload_fired__')
        } catch {
          /* noop */
        }

        for (const row of data) {
          // Use ref to get current state (avoids stale closure)
          const analysis = analysesRef.current.find(a => a.id === row.analysis_id)
          if (!analysis || analysis.status !== 'processing') continue

          if (row.status === 'completed') {
            setAnalyses(prev => {
              const updated = prev.map(a =>
                a.id === row.analysis_id ? { ...a, status: 'completed' as const, scoreGlobal: row.score_global ?? undefined } : a
              )
              saveToStorage(updated)
              return updated
            })

            // Show completion notification (last-write-wins if multiple complete)
            setCompletedAnalysis({ ...analysis, status: 'completed', scoreGlobal: row.score_global ?? undefined })

            // Premium confetti
            confetti({
              particleCount: 80,
              spread: 60,
              origin: { y: 0.3 },
              colors: ['#a855f7', '#8b5cf6', '#c084fc', '#10b981'],
            })

            // Browser notification if tab not visible
            try {
              if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                const scoreText = row.score_global ? ` Score : ${row.score_global}/100` : ''
                const typeLabel = analysis.type === 'flash' ? 'Flash' : 'Approfondi'
                const n = new Notification('Better/Close', {
                  body: `Analyse ${typeLabel} terminee !${scoreText}`,
                  icon: '/favicon.ico',
                })
                n.onclick = () => { window.focus(); n.close() }
              }
            } catch { /* Notification API unavailable */ }
          } else if (row.status === 'failed') {
            setAnalyses(prev => {
              const updated = prev.filter(a => a.id !== row.analysis_id)
              saveToStorage(updated)
              return updated
            })
            toast.error('Analyse échouée. Vos crédits ont été remboursés.', { duration: 6000 })
          }
        }
      } catch (_e) {
        if (abortController.signal.aborted) return
        consecutiveErrors++
        if (consecutiveErrors >= 10) {
          // 30 seconds of failures = client is dead. Reload the page so
          // the user gets a fresh Supabase client. Guarded by a
          // sessionStorage one-shot flag (same pattern as src/main.tsx
          // chunk recovery) to prevent an infinite reload loop when the
          // backend is persistently down. The guard is cleared on any
          // successful poll so the safety net re-arms automatically.
          try {
            if (sessionStorage.getItem('__bc_active_analysis_reload_fired__') === '1') {
              console.warn('[ActiveAnalysis] Already reloaded once this session, backing off to avoid loop')
              // Reset the error counter so we don't hammer the retry
              // logic; the user will see their in-flight pill but no
              // forced reload until the next session.
              consecutiveErrors = 0
              return
            }
            sessionStorage.setItem('__bc_active_analysis_reload_fired__', '1')
          } catch {
            /* sessionStorage unavailable — still safer to reload once than leave user stuck */
          }
          window.location.reload()
          return
        }
      } finally {
        inFlight = false
      }
    }

    // Poll immediately, then every 3s
    pollFn()
    pollRef.current = setInterval(pollFn, 3000)

    return () => {
      // Abort in-flight queries first — prevents dangling requests
      // from holding the Supabase auth lock after cleanup
      abortController.abort()
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [processingIds, saveToStorage])

  return (
    <ActiveAnalysisContext.Provider value={{
      analyses,
      addAnalysis,
      addActivity,
      dismissAnalysis,
      completedAnalysis,
      dismissCompleted,
    }}>
      {children}
    </ActiveAnalysisContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActiveAnalysis() {
  const context = useContext(ActiveAnalysisContext)
  if (!context) {
    throw new Error('useActiveAnalysis must be used within ActiveAnalysisProvider')
  }
  return context
}
